import { Portfolio, User, UserStatus, UserStatusNames } from '@app/models';
import React, {
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { calculatePortfolios } from '@app/helpers';
import avatar from 'assets/images/avatar.png';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar';
import { Separator } from 'components/ui/separator';
import { MapPin, Briefcase, Calendar } from 'lucide-react';
import { getFileById } from '@app/api';
import { cn } from 'lib/utils';

interface CardProps extends Partial<User> {
  title?: string;
  description?: string;
  price?: string;
  onClick?: () => void;
  horizontal?: boolean;
  portfolios: Portfolio[];
  invertBgColorCard?: boolean;
}

const InfoRow = ({
  label,
  value,
  icon: Icon,
  className = '',
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}) => (
  <div className={cn('flex flex-col space-y-1.5 min-w-0 group', className)}>
    <div className="flex items-center gap-1.5">
      {Icon && (
        <Icon className="w-3 h-3 text-muted-foreground/60 group-hover:text-muted-foreground transition-colors" />
      )}
      <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
        {label}
      </span>
    </div>
    <div className="text-sm font-medium text-foreground break-words hyphens-auto pl-0">
      {value}
    </div>
  </div>
);

const UserAvatar = ({
  imageIds,
  placeholder,
  fullName,
}: {
  imageIds: string[];
  placeholder: string;
  fullName: string;
}) => {
  const [src, setSrc] = useState<string>(placeholder);
  const [isLoading, setIsLoading] = useState(false);
  const mainImageId = imageIds && imageIds.length > 0 ? imageIds[0] : null;
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    if (mainImageId) {
      setIsLoading(true);
      getFileById(mainImageId)
        .then((blob) => {
          if (!isMounted) return;
          const url = URL.createObjectURL(blob);
          urlRef.current = url;
          setSrc(url);
        })
        .catch(() => setSrc(placeholder))
        .finally(() => setIsLoading(false));
    } else {
      setSrc(placeholder);
    }
    return () => {
      isMounted = false;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
    };
  }, [mainImageId, placeholder]);

  const initials = fullName
    .split(' ')
    .map((name) => name.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="relative">
      <Avatar className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl border-2 border-border/50 shadow-sm transition-all duration-300 hover:shadow-md hover:border-border group-hover:scale-105">
        <AvatarImage
          src={src}
          className={cn(
            'object-cover transition-opacity duration-300',
            isLoading && 'opacity-50'
          )}
        />
        <AvatarFallback className="bg-gradient-to-br from-blue-50 to-indigo-100 text-blue-600 font-semibold text-sm dark:from-blue-950 dark:to-indigo-900 dark:text-blue-300">
          {initials || '?'}
        </AvatarFallback>
      </Avatar>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

function UserCard(props: CardProps) {
  const {
    firstName = '',
    lastName = '',
    middleName = '',
    specializationOked,
    kato,
    imageIds,
    avatarFilesIds,
    status,
    onClick,
    portfolios,
    invertBgColorCard = false,
  } = props;

  const [isHovered, setIsHovered] = useState(false);

  const onClickCard = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onClick) return;
      onClick();
    },
    [onClick]
  );

  const fullName = useMemo(
    () => `${firstName} ${middleName} ${lastName}`.trim(),
    [firstName, lastName, middleName]
  );

  const portfolioDate = useMemo(() => {
    return calculatePortfolios(portfolios);
  }, [portfolios]);

  const images = useMemo(() => {
    if (imageIds) return imageIds.split(',');
    if (avatarFilesIds) return avatarFilesIds.split(',');
    return [];
  }, [avatarFilesIds, imageIds]);

  const isBusy = status === UserStatus.BUSY;

  return (
    <Card
      className={cn(
        'w-full group cursor-pointer overflow-hidden',
        'transition-all duration-300 ease-out',
        'hover:shadow-lg hover:shadow-black/5',
        'hover:-translate-y-1 hover:scale-[1.01]',
        'border-border/50 hover:border-border',
        invertBgColorCard ? 'bg-muted/30' : 'bg-card'
      )}
      onClick={onClickCard}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col sm:hidden space-y-4">
          <div className="flex items-center space-x-3">
            <UserAvatar
              imageIds={images}
              placeholder={avatar}
              fullName={fullName}
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base leading-tight break-words group-hover:text-primary transition-colors">
                {fullName}
              </h3>
              <div className="flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3 text-muted-foreground/60" />
                <p className="text-sm text-muted-foreground">
                  {kato?.nameRu || 'Не указан'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <Badge
              variant={isBusy ? 'destructive' : 'default'}
              className={cn(
                'text-xs font-medium transition-all duration-300',
                isBusy
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white',
                isHovered && 'scale-105 shadow-sm'
              )}
            >
              {UserStatusNames[status || UserStatus.BUSY]}
            </Badge>
          </div>

          <div className="space-y-4">
            <InfoRow
              label="Специализация"
              value={specializationOked?.nameRu || 'Не указана'}
              icon={Briefcase}
            />
            <InfoRow
              label="Опыт работы"
              value={portfolioDate}
              icon={Calendar}
            />
          </div>

          {status && (
            <Button
              onClick={onClick}
              className="w-full transition-all duration-300 hover:shadow-md"
              size="sm"
            >
              Подробнее
            </Button>
          )}
        </div>

        <div className="hidden sm:flex lg:hidden items-center space-x-6">
          <div className="flex-shrink-0">
            <UserAvatar
              imageIds={images}
              placeholder={avatar}
              fullName={fullName}
            />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg leading-tight break-words mb-2 group-hover:text-primary transition-colors">
              {fullName}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow
                label="Регион"
                value={kato?.nameRu || 'Не указан'}
                icon={MapPin}
              />
              <InfoRow
                label="Специализация"
                value={specializationOked?.nameRu || 'Не указана'}
                icon={Briefcase}
              />
              <InfoRow
                label="Опыт работы"
                value={portfolioDate}
                icon={Calendar}
                className="col-span-2"
              />
            </div>
          </div>

          <div className="flex-shrink-0 flex flex-col items-center space-y-3">
            <Badge
              variant={isBusy ? 'destructive' : 'default'}
              className={cn(
                'text-xs font-medium transition-all duration-300',
                isBusy
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white',
                isHovered && 'scale-105 shadow-sm'
              )}
            >
              {UserStatusNames[status || UserStatus.BUSY]}
            </Badge>
            {status && (
              <Button
                onClick={onClick}
                size="sm"
                className="transition-all duration-300 hover:shadow-md"
              >
                Подробнее
              </Button>
            )}
          </div>
        </div>

        <div className="hidden lg:flex items-center space-x-6">
          <div className="flex-shrink-0">
            <UserAvatar
              imageIds={images}
              placeholder={avatar}
              fullName={fullName}
            />
          </div>

          <Separator
            orientation="vertical"
            className={cn(
              'h-20 transition-all duration-300',
              isHovered && 'bg-border'
            )}
          />

          <div className="flex-1 min-w-0">
            <div className="grid grid-cols-4 gap-4">
              <InfoRow
                label="Ф.И.О."
                value={
                  <span className="group-hover:text-primary transition-colors font-medium">
                    {fullName}
                  </span>
                }
              />
              <InfoRow
                label="Регион"
                value={kato?.nameRu || 'Не указан'}
                icon={MapPin}
              />
              <InfoRow
                label="Специализация"
                value={specializationOked?.nameRu || 'Не указана'}
                icon={Briefcase}
              />
              <InfoRow
                label="Опыт работы"
                value={portfolioDate}
                icon={Calendar}
              />
            </div>
          </div>

          <div className="flex-shrink-0 flex flex-col items-center space-y-3">
            <Badge
              variant={isBusy ? 'destructive' : 'default'}
              className={cn(
                'text-xs font-medium transition-all duration-300 w-24 justify-center',
                isBusy
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white',
                isHovered && 'scale-105 shadow-sm'
              )}
            >
              {UserStatusNames[status || UserStatus.BUSY]}
            </Badge>
            {status && (
              <Button
                onClick={onClick}
                size="sm"
                className="w-24 transition-all duration-300 hover:shadow-md"
              >
                Подробнее
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default memo(UserCard);
